import { useCallback, useEffect } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import Accordion from '@/components/atoms/Accordion/Accordion';
import AvatarUpload from '@/components/atoms/Avatar/AvatarUpload/AvatarUpload';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useIntegrations } from '@/hooks/useIntegrations';
import usePrompt from '@/hooks/usePrompt';
import { cn } from '@/lib/utils';
import { setDirty, setExpanded } from '@/redux/integrations/actions';
import { selectAccordion } from '@/redux/integrations/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { AccordionFieldsetProps, SuncoIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { errorMessage, trimOnBlur } from '@/util/validation/helpers';
import { suncoIntegration } from '@/util/validation/rules/integration-rules';

import styles from './SettingsAccordion.module.scss';
type Form = { welcome_trigger_message: string; name: string; avatar_url: string };

export const SettingsAccordion = ({
  integration,
  type,
  toggleAccordion,
  registerAccordion,
}: AccordionFieldsetProps<Form, SuncoIntegration>) => {
  const { t } = useTranslation();
  const { expanded } = useSelector(selectAccordion);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const dispatch = useDispatch();
  const { updateIntegration, updateStatus } = useIntegrations<SuncoIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );

  const formMethods = useForm<Form>({ mode: 'onChange' });
  const {
    formState: { errors, isDirty },
    handleSubmit,
    getValues,
    trigger,
    reset,
    setValue,
    control,
  } = formMethods;

  const avatarUrl = useWatch({ control, name: 'avatar_url' });

  useEffect(() => {
    reset({
      name: integration?.name || '',
      welcome_trigger_message: integration?.config?.welcome_trigger_message || '',
      avatar_url: integration?.config?.avatar || '',
    });
  }, [
    integration?.config?.avatar,
    integration?.config?.welcome_trigger_message,
    integration?.name,
    reset,
  ]);

  const handleUpdateIntegration = useCallback(() => {
    const { name, welcome_trigger_message, avatar_url } = getValues();
    updateIntegration(
      {
        ...integration,
        name,
        config: {
          ...integration?.config,
          welcome_trigger_message: welcome_trigger_message || undefined,
          avatar: avatar_url || undefined,
        },
      },
      {
        onSettled: () => {
          if (expanded === type) {
            dispatch(setExpanded(false));
          }
        },
      }
    );
  }, [dispatch, expanded, getValues, integration, type, updateIntegration]);

  usePrompt(isDirty && canWrite, undefined, undefined, handleSubmit(handleUpdateIntegration));

  useEffect(() => {
    dispatch(setDirty(isDirty));
  }, [dispatch, isDirty]);

  useEffect(() => {
    if (expanded !== type && isDirty) {
      reset();
    }
  }, [expanded, isDirty, reset, type]);

  const handleAvatarUpdate = useCallback(
    (data) => {
      setValue('avatar_url', data?.avatar, { shouldDirty: true });
    },
    [setValue]
  );

  return (
    <Accordion
      title={t('integrations.sunco.settings.title')}
      subtitle={t('integrations.sunco.settings.subtitle')}
      disabled={!isDirty}
      isLoading={updateStatus === 'pending'}
      expanded={expanded === type}
      handleChange={toggleAccordion(type)}
      onSubmit={handleSubmit(handleUpdateIntegration)}
      readOnly={!canWrite}
      ref={registerAccordion(type, handleSubmit(handleUpdateIntegration), trigger)}
    >
      <Form {...formMethods}>
        <div className="mb-6 space-y-6">
          <FormField
            control={control}
            name="name"
            rules={suncoIntegration.name}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('integrations.sunco.settings.name')}</FormLabel>
                <FormControl>
                  <Input {...field} onBlur={trimOnBlur(field)} readOnly={!canWrite} />
                </FormControl>
                <FormMessage>
                  {errorMessage({
                    field: errors['name'],
                    maxLength: suncoIntegration.name.maxLength,
                  })}
                </FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="welcome_trigger_message"
            rules={suncoIntegration.welcome_trigger_message}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('integrations.sunco.settings.welcome_trigger_message')}</FormLabel>
                <FormControl>
                  <Input {...field} onBlur={trimOnBlur(field)} readOnly={!canWrite} />
                </FormControl>
                <FormMessage>
                  {errorMessage({
                    field: errors['welcome_trigger_message'],
                    maxLength: suncoIntegration.welcome_trigger_message.maxLength,
                  })}
                </FormMessage>
              </FormItem>
            )}
          />
        </div>
      </Form>
      <div className={cn(styles.wrapper, { [styles.disabled]: !canWrite })}>
        <div className={styles.title}>{t('integrations.web_ui.avatar')}</div>
        <AvatarUpload onUpdate={handleAvatarUpdate} label="userAvatar" avatar={avatarUrl} />
      </div>
    </Accordion>
  );
};
